import { useMemo } from 'react';

interface LetterScrollProps {
  subject: string | null;
  preview: string;
  date: string;
  isSelected?: boolean;
  onClick: () => void;
  index?: number;
  thumbnailUrl?: string | null;
  direction?: 'in' | 'out';
}

/**
 * A parchment-styled letter item with random tilt
 * Designed to feel like scattered papers in a letterbox
 */
export function LetterScroll({
  subject,
  preview,
  date,
  isSelected,
  onClick,
  index = 0,
  thumbnailUrl,
  direction,
}: LetterScrollProps) {
  // Staggered entrance animation delay
  const animationDelay = `${index * 50}ms`;

  // Random tilt based on index (deterministic so it doesn't change on re-render)
  const tilt = useMemo(() => {
    const seed = index * 7 + 3;
    return ((seed % 7) - 3) * 0.8; // Range: roughly -2.4 to +2.4 degrees
  }, [index]);

  const isFromCorrespondent = direction === 'out';
  const hasThumbnail = isFromCorrespondent && thumbnailUrl;

  return (
    <div
      className="relative"
      style={{
        transform: `rotate(${tilt}deg)`,
        transition: 'transform 0.2s ease',
      }}
    >
      <button
        onClick={onClick}
        className={`
          relative w-full text-left rounded-sm transition-all duration-200
          animate-fade-in-up
          ${isSelected ? 'z-10' : 'hover:z-10'}
        `}
        style={{
          animationDelay,
          animationFillMode: 'backwards',
          transform: isSelected
            ? 'translateY(-4px) scale(1.02)'
            : undefined,
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = '';
          }
        }}
      >
      {/* Parchment base with texture */}
      <div
        className={`
          absolute inset-0 rounded-sm transition-all duration-200
          ${isSelected ? 'shadow-lg shadow-amber-900/40' : 'shadow-md shadow-black/30'}
        `}
        style={{
          background: `
            linear-gradient(135deg,
              #f5e6c8 0%,
              #f0dbb8 25%,
              #e8d4b0 50%,
              #f2dfc0 75%,
              #ebe0c0 100%
            )
          `,
        }}
      />

      {/* Parchment texture overlay */}
      <div
        className="absolute inset-0 rounded-sm opacity-30"
        style={{
          backgroundImage: `
            radial-gradient(ellipse at 20% 30%, rgba(139, 90, 43, 0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(139, 90, 43, 0.1) 0%, transparent 40%),
            radial-gradient(ellipse at 50% 50%, rgba(180, 140, 80, 0.08) 0%, transparent 60%)
          `,
        }}
      />

      {/* Aged edges effect */}
      <div
        className="absolute inset-0 rounded-sm opacity-40"
        style={{
          boxShadow: `
            inset 2px 2px 4px rgba(139, 90, 43, 0.2),
            inset -2px -2px 4px rgba(139, 90, 43, 0.15)
          `,
        }}
      />

      {/* Selection glow */}
      {isSelected && (
        <div
          className="absolute inset-0 rounded"
          style={{
            boxShadow: '0 0 0 2px rgba(92, 61, 46, 0.6)',
          }}
        />
      )}

      {/* Content */}
      <div className="relative p-3 flex gap-3">
        {/* Letter text content */}
        <div className="flex-1 min-w-0">
          <div
            className="font-display text-sm mb-1 truncate"
            style={{ color: '#3d2914' }}
          >
            {subject || 'An Untitled Missive'}
          </div>
          <div
            className="text-xs line-clamp-2 font-serif italic"
            style={{ color: '#5c4023' }}
          >
            {preview}
          </div>
          <div
            className="text-xs mt-2"
            style={{ color: '#8b6b43' }}
          >
            {formatMysticalDate(date)}
          </div>
        </div>

        {/* Thumbnail on the right for letters from correspondent */}
        {hasThumbnail && (
          <div
            className="flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden"
            style={{
              boxShadow: 'inset 0 0 4px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)',
            }}
          >
            <img
              src={thumbnailUrl}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )}
      </div>
      </button>
    </div>
  );
}

/**
 * Format date in a mystical, non-technical way
 */
function formatMysticalDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'This very day';
  } else if (diffDays === 1) {
    return 'The day before';
  } else if (diffDays === 2) {
    return 'Two moons past';
  } else if (diffDays < 7) {
    return `${diffDays} days past`;
  } else if (diffDays < 14) {
    return 'A fortnight ago';
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
    });
  }
}
